﻿/**
 * AOCTANT PDP Product Gallery
 *
 * Custom media gallery behavior:
 * - Shopify product.media slide synchronization
 * - prev/next arrows + thumbnail selection
 * - native video play/pause lifecycle
 * - full-screen modal viewer with counter
 * - image zoom/pan
 * - keyboard + focus restoration
 *
 * No duplicate product/variant/cart state.
 */

class AoctantProductGallery extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.mediaCount = 0;
    this.lastFocus = null;
    this.isZoomed = false;
    this.mobileZoomQuery = window.matchMedia('(max-width: 749px)');
    this.zoomStates = new WeakMap();
    this.suppressZoomClick = false;
    this.scrollTimeout = null;
    this.handleProductSelect = this.handleProductSelect.bind(this);
  }

  connectedCallback() {
    this.eventsController?.abort();
    this.eventsController = new AbortController();
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener('shopify:product:select', this.handleProductSelect, {
      signal: this.eventsController.signal,
    });
    this.track = this.querySelector('[data-track]');
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.thumbnails = Array.from(this.querySelectorAll('[data-thumbnail-index]'));
    this.prevButton = this.querySelector('[data-prev]');
    this.nextButton = this.querySelector('[data-next]');

    this.modal = this.querySelector('[data-modal]');
    this.modalTrack = this.querySelector('[data-modal-track]');
    this.modalSlides = Array.from(this.querySelectorAll('[data-modal-slide]'));
    this.modalClose = this.querySelector('[data-modal-close]');
    this.modalPrev = this.querySelector('[data-modal-prev]');
    this.modalNext = this.querySelector('[data-modal-next]');
    this.modalBackdrop = this.querySelector('[data-modal-backdrop]');
    this.counterCurrent = this.querySelector('[data-modal-counter-current]');
    this.counterTotal = this.querySelector('[data-modal-counter-total]');
    this.openModalButtons = Array.from(this.querySelectorAll('[data-open-modal]'));
    this.zoomWrappers = Array.from(this.querySelectorAll('[data-zoom-wrapper]'));

    this.mediaCount = parseInt(this.dataset.mediaCount, 10) || this.slides.length || 0;
    this.currentIndex = parseInt(this.dataset.initialIndex, 10) || 0;

    if (this.mediaCount === 0) return;

    this.bindEvents();
    this.bindArrowProximity();
    this.selectMedia(this.currentIndex, { animate: false });
  }

  disconnectedCallback() {
    this.eventsController?.abort();
    if (this.modal?.open) {
      this.modal.close();
      this.clearModal();
    }
    this.slides?.forEach((slide) => {
      slide.querySelectorAll('[data-video-element]').forEach((video) => video.pause?.());
    });
    this.lastFocus = null;
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    this.scrollTimeout = null;
  }

  bindArrowProximity() {
    const { signal } = this.eventsController;
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    const stage = this.querySelector('.aoctant-product-gallery__stage');
    let pointer = null;
    const update = () => {
      const bounds = stage?.getBoundingClientRect();
      const near = finePointer.matches && pointer && bounds &&
        pointer.x >= bounds.left - bounds.width * .2 &&
        pointer.x <= bounds.right + bounds.width * .2 &&
        pointer.y >= bounds.top - bounds.height * .2 &&
        pointer.y <= bounds.bottom + bounds.height * .2;
      this.toggleAttribute('data-arrow-proximity', Boolean(near));
    };
    document.addEventListener('pointermove', (event) => {
      pointer = event.pointerType === 'touch' ? null : { x: event.clientX, y: event.clientY };
      update();
    }, { passive: true, signal });
    document.addEventListener('pointerout', (event) => {
      if (event.relatedTarget) return;
      pointer = null;
      update();
    }, { signal });
    window.addEventListener('blur', () => { pointer = null; update(); }, { signal });
    window.addEventListener('scroll', update, { passive: true, capture: true, signal });
    window.addEventListener('resize', update, { passive: true, signal });
    finePointer.addEventListener('change', update, { signal });
    update();
  }

  bindEvents() {
    const { signal } = this.eventsController;
    // Main gallery controls
    this.prevButton?.addEventListener('click', () => this.selectMedia(this.currentIndex - 1), { signal });
    this.nextButton?.addEventListener('click', () => this.selectMedia(this.currentIndex + 1), { signal });

    this.thumbnails.forEach((button) => {
      button.addEventListener('click', () => {
        const index = parseInt(button.dataset.thumbnailIndex, 10);
        this.selectMedia(index);
      }, { signal });
    });

    // A real swipe/drag/wheel takes over from any programmatic navigation still in flight.
    const releaseProgrammaticScroll = () => {
      this.pendingScrollIndex = null;
    };
    ['pointerdown', 'touchstart', 'wheel'].forEach((type) => {
      this.track?.addEventListener(type, releaseProgrammaticScroll, { passive: true, signal });
    });

    // Scroll-snap detection for touch/horizontal navigation
    this.track?.addEventListener(
      'scroll',
      () => {
        if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => this.handleScrollSnap(), 150);
      },
      { passive: true, signal }
    );

    // Modal triggers
    this.openModalButtons.forEach((button) => {
      button.addEventListener('click', () => this.openModal(this.currentIndex), { signal });
    });

    // Modal controls
    this.modalClose?.addEventListener('click', () => this.closeModal(), { signal });
    this.modalPrev?.addEventListener('click', () => this.modalNavigate(this.currentIndex - 1), { signal });
    this.modalNext?.addEventListener('click', () => this.modalNavigate(this.currentIndex + 1), { signal });
    this.modalBackdrop?.addEventListener('click', () => this.closeModal(), { signal });

    // Zoom
    this.zoomWrappers.forEach((wrapper) => {
      wrapper.addEventListener('click', () => {
        if (this.suppressZoomClick) {
          this.suppressZoomClick = false;
          return;
        }
        wrapper.classList.remove('is-gesture-settled');
        this.toggleZoom(wrapper);
      }, { signal });
      wrapper.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          wrapper.classList.remove('is-gesture-settled');
          this.toggleZoom(wrapper);
        }
      }, { signal });
      this.bindMobileZoomGestures(wrapper, signal);
    });

    const syncZoomToViewport = () => {
      if (!this.modal?.open) return;
      if (!this.mobileZoomQuery.matches) {
        this.resetZoom();
        return;
      }

      this.zoomWrappers.forEach((wrapper) => {
        const state = this.zoomStates.get(wrapper);
        if (!state || state.scale <= 1) return;
        wrapper.classList.add('is-gesture-settled');
        this.setMobileZoom(wrapper, state.scale, state.x, state.y);
      });
    };
    window.addEventListener('resize', syncZoomToViewport, { signal });
    this.mobileZoomQuery.addEventListener('change', syncZoomToViewport, { signal });
    window.visualViewport?.addEventListener('resize', syncZoomToViewport, { signal });

    // Keyboard
    this.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
    this.modal?.addEventListener('keydown', (event) => this.handleModalKeydown(event), { signal });
    this.modal?.addEventListener('close', () => this.onModalClose(), { signal });
    this.modal?.addEventListener('cancel', () => this.onModalClose(), { signal });

    // Resize handling
    if ('ResizeObserver' in window && this.track) {
      this.resizeObserver = new ResizeObserver(() => {
        this.scrollToSlide(this.currentIndex, false);
      });
      this.resizeObserver.observe(this.track);
    }
  }

  handleProductSelect(event) {
    if (!event.promise || this.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        const featuredMediaId = detail?.resource?.featured_media?.id;
        if (!featuredMediaId) return;

        const targetIndex = this.slides.findIndex(
          (slide) => String(slide.dataset.mediaId) === String(featuredMediaId)
        );

        if (targetIndex < 0) return;

        this.selectMedia(targetIndex);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[aoctant-product-gallery] Variant media sync failed:', error);
        }
      });
  }

  selectMedia(index, options = {}) {
    if (index < 0 || index >= this.mediaCount || index === this.currentIndex) {
      if (index !== this.currentIndex) return;
    }

    const previousIndex = this.currentIndex;
    this.currentIndex = index;
    const animate = options.animate !== false;

    // Sync slides
    this.slides.forEach((slide, i) => {
      const isCurrent = i === index;
      slide.setAttribute('aria-current', isCurrent ? 'true' : 'false');
      slide.setAttribute('aria-hidden', isCurrent ? 'false' : 'true');
      slide.classList.toggle('is-active', isCurrent);
    });

    // Scroll main track
    this.scrollToSlide(index, animate);

    // Sync thumbnails
    this.thumbnails.forEach((thumb, i) => {
      const isCurrent = i === index;
      thumb.setAttribute('aria-current', isCurrent ? 'true' : 'false');
      thumb.setAttribute('aria-selected', isCurrent ? 'true' : 'false');
      thumb.setAttribute('tabindex', isCurrent ? '0' : '-1');
      thumb.classList.toggle('is-active', isCurrent);

      if (isCurrent && typeof thumb.scrollIntoView === 'function') {
        thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    });

    // Sync arrows
    if (this.prevButton) this.prevButton.disabled = index === 0;
    if (this.nextButton) this.nextButton.disabled = index === this.mediaCount - 1;

    // Video lifecycle
    this.updateVideoLifecycle(previousIndex, index);

    // Sync modal if open
    if (this.modal?.open) {
      this.updateModalSlide(index);
    }
  }

  scrollToSlide(index, animate = true) {
    if (!this.track) return;
    const slide = this.slides[index];
    if (!slide) return;

    // Already there: no scroll will follow, so nothing is in flight.
    if (Math.abs(this.track.scrollLeft - slide.offsetLeft) < 1) {
      this.pendingScrollIndex = null;
      return;
    }

    // Remember the destination so the debounced scroll-snap handler cannot mistake a stale or
    // in-between position (e.g. a timer armed by the previous scroll) for a swipe and revert it.
    this.pendingScrollIndex = index;
    this.pendingScrollSince = performance.now();

    if (animate) {
      this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      return;
    }

    // Non-animated positioning must be instant. behavior 'auto' defers to the track's CSS
    // scroll-behavior: smooth on touch layouts, which let the debounced scroll-snap handler read a
    // stale position and re-select the previous slide. Override inline for this one assignment.
    const { scrollBehavior } = this.track.style;
    this.track.style.scrollBehavior = 'auto';
    this.track.scrollLeft = slide.offsetLeft;
    this.track.style.scrollBehavior = scrollBehavior;
  }

  handleScrollSnap() {
    if (!this.track || this.mediaCount <= 1) return;

    const slideWidth = this.track.clientWidth;
    if (!slideWidth) return;

    const newIndex = Math.round(this.track.scrollLeft / slideWidth);

    // Programmatic navigation in flight: wait until it arrives, never revert it. If it never
    // arrives (interrupted by the browser), stop waiting after a second and follow the position.
    if (this.pendingScrollIndex != null) {
      if (newIndex === this.pendingScrollIndex) {
        this.pendingScrollIndex = null;
        return;
      }
      if (performance.now() - (this.pendingScrollSince || 0) < 1000) return;
      this.pendingScrollIndex = null;
    }

    if (newIndex >= 0 && newIndex < this.mediaCount && newIndex !== this.currentIndex) {
      this.selectMedia(newIndex, { animate: false });
    }
  }

  updateVideoLifecycle(previousIndex, currentIndex) {
    const currentSlide = this.slides[currentIndex];
    const currentVideo = currentSlide?.querySelector('[data-video-element]');

    // Avoid pausing then immediately re-playing the same video on initial load.
    if (previousIndex !== currentIndex) {
      const previousSlide = this.slides[previousIndex];
      const previousVideo = previousSlide?.querySelector('[data-video-element]');
      if (previousVideo && previousVideo !== currentVideo && typeof previousVideo.pause === 'function') {
        previousVideo.pause();
      }
    }

    if (currentVideo && typeof currentVideo.play === 'function') {
      const playPromise = currentVideo.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          // Autoplay may be blocked; native controls remain available.
        });
      }
    }
  }

  openModal(index) {
    if (!this.modal || this.mediaCount === 0) return;

    this.lastFocus = document.activeElement;
    this.resetZoom();

    // Pause the main-stage video before the modal copy starts playing.
    this.pauseMainStageVideo(index);
    this.updateModalSlide(index);

    this.modal.showModal();
    document.body.classList.add('aoctant-gallery-modal-open');

    // Move focus to the close button for discoverability
    requestAnimationFrame(() => {
      if (this.isConnected && this.modal?.open) this.modalClose?.focus();
    });
  }

  pauseMainStageVideo(index) {
    const slide = this.slides[index];
    const video = slide?.querySelector('[data-video-element]');
    if (video && typeof video.pause === 'function') {
      video.pause();
    }
  }

  resumeMainStageVideo(index) {
    const slide = this.slides[index];
    const video = slide?.querySelector('[data-video-element]');
    if (video && typeof video.play === 'function') {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }
  }

  closeModal() {
    if (!this.modal?.open) return;
    this.modal.close();
  }

  onModalClose() {
    this.clearModal();
    if (!this.isConnected) return;

    const focusTarget = this.lastFocus;
    this.lastFocus = null;

    // Restore focus to the invoking trigger
    if (focusTarget && typeof focusTarget.focus === 'function') {
      requestAnimationFrame(() => {
        if (this.isConnected && focusTarget.isConnected && !this.modal?.open) focusTarget.focus();
      });
    }

    // Resume the main-stage video for the current slide.
    this.resumeMainStageVideo(this.currentIndex);
  }

  clearModal() {
    document.body.classList.remove('aoctant-gallery-modal-open');

    // Pause all modal videos
    this.modal.querySelectorAll('[data-modal-video]').forEach((video) => {
      if (typeof video.pause === 'function') video.pause();
    });

    this.resetZoom();
  }

  modalNavigate(index) {
    if (index < 0 || index >= this.mediaCount) return;
    this.selectMedia(index);
  }

  updateModalSlide(index) {
    this.modalSlides.forEach((slide, i) => {
      const isCurrent = i === index;
      slide.setAttribute('aria-hidden', isCurrent ? 'false' : 'true');
      slide.classList.toggle('is-active', isCurrent);
    });

    if (this.counterCurrent) this.counterCurrent.textContent = index + 1;
    if (this.modalPrev) this.modalPrev.disabled = index === 0;
    if (this.modalNext) this.modalNext.disabled = index === this.mediaCount - 1;

    // Modal video lifecycle
    this.modalSlides.forEach((slide, i) => {
      const video = slide.querySelector('[data-modal-video]');
      if (!video || typeof video.play !== 'function') return;

      if (i === index) {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      } else if (typeof video.pause === 'function') {
        video.pause();
      }
    });

    this.resetZoom();
  }

  handleKeydown(event) {
    if (this.modal?.open && this.modal.contains(event.target)) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.selectMedia(this.currentIndex - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.selectMedia(this.currentIndex + 1);
    }
  }

  handleModalKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeModal();
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.modalNavigate(this.currentIndex - 1);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.modalNavigate(this.currentIndex + 1);
      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  trapFocus(event) {
    const focusable = Array.from(
      this.modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null);

    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  bindMobileZoomGestures(wrapper, signal) {
    const pointers = new Map();
    let gesture = null;
    let gestureMoved = false;

    const getPoint = (event) => ({ x: event.clientX, y: event.clientY });
    const getMidpoint = () => {
      const points = Array.from(pointers.values());
      return {
        x: (points[0].x + points[1].x) / 2,
        y: (points[0].y + points[1].y) / 2,
      };
    };
    const getDistance = () => {
      const points = Array.from(pointers.values());
      return Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
    };
    const rememberGesture = () => {
      const state = this.zoomStates.get(wrapper) ?? { scale: 1, x: 0, y: 0 };
      const points = Array.from(pointers.values());

      if (points.length >= 2) {
        gesture = {
          type: 'pinch',
          distance: Math.max(1, getDistance()),
          midpoint: getMidpoint(),
          scale: state.scale,
          x: state.x,
          y: state.y,
        };
      } else if (points.length === 1) {
        gesture = {
          type: 'pan',
          point: points[0],
          scale: state.scale,
          x: state.x,
          y: state.y,
        };
      } else {
        gesture = null;
      }
    };

    wrapper.addEventListener('pointerdown', (event) => {
      if (!this.mobileZoomQuery.matches || event.pointerType !== 'touch') return;
      pointers.set(event.pointerId, getPoint(event));
      wrapper.classList.remove('is-gesture-settled');
      try {
        wrapper.setPointerCapture(event.pointerId);
      } catch {
        // Synthetic or cancelled pointers may not support capture.
      }
      if (pointers.size === 1) gestureMoved = false;
      rememberGesture();
      wrapper.classList.add('is-gesturing');
    }, { signal });

    wrapper.addEventListener('pointermove', (event) => {
      if (!this.mobileZoomQuery.matches || !pointers.has(event.pointerId) || !gesture) return;
      pointers.set(event.pointerId, getPoint(event));

      if (pointers.size >= 2 && gesture.type === 'pinch') {
        const midpoint = getMidpoint();
        const distance = getDistance();
        const nextScale = Math.max(1, Math.min(4, gesture.scale * distance / gesture.distance));
        const rect = wrapper.getBoundingClientRect();
        const midpointX = midpoint.x - (rect.left + rect.width / 2);
        const midpointY = midpoint.y - (rect.top + rect.height / 2);
        const startMidpointX = gesture.midpoint.x - (rect.left + rect.width / 2);
        const startMidpointY = gesture.midpoint.y - (rect.top + rect.height / 2);
        const ratio = nextScale / gesture.scale;
        const nextX = midpointX - ratio * (startMidpointX - gesture.x);
        const nextY = midpointY - ratio * (startMidpointY - gesture.y);

        if (Math.abs(distance - gesture.distance) > 2) gestureMoved = true;
        this.setMobileZoom(wrapper, nextScale, nextX, nextY);
      } else if (pointers.size === 1 && gesture.type === 'pan') {
        const point = pointers.get(event.pointerId);
        const deltaX = point.x - gesture.point.x;
        const deltaY = point.y - gesture.point.y;
        if (Math.hypot(deltaX, deltaY) > 3) gestureMoved = true;
        if (gesture.scale > 1) {
          this.setMobileZoom(wrapper, gesture.scale, gesture.x + deltaX, gesture.y + deltaY);
        }
      }

      if (gestureMoved) wrapper.classList.add('is-gesturing');
    }, { signal });

    const finishPointer = (event) => {
      if (!pointers.has(event.pointerId)) return;
      if (gestureMoved) {
        this.suppressZoomClick = true;
        window.setTimeout(() => { this.suppressZoomClick = false; }, 0);
      }
      pointers.delete(event.pointerId);
      rememberGesture();
      if (pointers.size === 0) {
        wrapper.classList.remove('is-gesturing');
        if (gestureMoved) wrapper.classList.add('is-gesture-settled');
      }
    };

    wrapper.addEventListener('pointerup', finishPointer, { signal });
    wrapper.addEventListener('pointercancel', finishPointer, { signal });
  }

  setMobileZoom(wrapper, scale, x, y) {
    const image = wrapper.querySelector('[data-modal-image]');
    if (!image) return;

    if (scale <= 1.01) {
      this.zoomStates.set(wrapper, { scale: 1, x: 0, y: 0 });
      wrapper.classList.remove('is-zoomed');
      image.style.transform = '';
      this.isZoomed = false;
      return;
    }

    const wrapperRect = wrapper.getBoundingClientRect();
    const maxX = Math.max(0, (image.offsetWidth * scale - wrapperRect.width) / 2);
    const maxY = Math.max(0, (image.offsetHeight * scale - wrapperRect.height) / 2);
    const translateX = Math.max(-maxX, Math.min(maxX, x));
    const translateY = Math.max(-maxY, Math.min(maxY, y));
    const state = { scale, x: translateX, y: translateY };

    this.zoomStates.set(wrapper, state);
    wrapper.classList.add('is-zoomed');
    image.style.transform = `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale})`;
    this.isZoomed = true;
  }

  toggleZoom(wrapper) {
    if (!wrapper) return;
    const image = wrapper.querySelector('[data-modal-image]');
    if (!image) return;

    if (this.mobileZoomQuery.matches) {
      const state = this.zoomStates.get(wrapper) ?? { scale: 1, x: 0, y: 0 };
      this.setMobileZoom(wrapper, state.scale > 1.01 ? 1 : 2, 0, 0);
      return;
    }

    this.isZoomed = !this.isZoomed;

    if (this.isZoomed) {
      wrapper.classList.add('is-zoomed');
      image.style.transform = 'scale(2)';
    } else {
      this.resetZoom();
    }
  }

  resetZoom() {
    this.isZoomed = false;
    this.modal?.querySelectorAll('[data-modal-image]').forEach((img) => {
      img.style.transform = '';
    });
    this.modal?.querySelectorAll('[data-zoom-wrapper]').forEach((wrapper) => {
      wrapper.classList.remove('is-zoomed', 'is-gesturing', 'is-gesture-settled');
    });
    this.zoomStates = new WeakMap();
  }
}

if (!customElements.get('aoctant-product-gallery')) {
  customElements.define('aoctant-product-gallery', AoctantProductGallery);
}
